<template>
	<div class="transactional-emails-wrapper height-100">
		<Row class="header flex-justify-space-between width-100 pa-1">
			<h2 class="font-size-lg font-weight-500 pt-1">{{ isRichTextMode ? 'Preview' : selectedTemplate?.title || 'Preview' }}</h2>
			<Row class="px-2">
				<template v-if="!isRichTextMode">
					<div @click="openChangeTemplateDialog" class="change-template-btn font-size-md text-underline cursor-pointer">
						Change template
					</div>
					<AnemicButton @click="openEditTemplateDialog" class="header-btn">Edit</AnemicButton>
					<AnemicButton @click="sendTest" class="header-btn">Send test...</AnemicButton>
				</template>
			</Row>
		</Row>
		<Col class="main-template-background">
			<Col class="email-template-content">
				<AppLoader v-if="isEmailContentLoading" class="height-100 width-100 flex-centerize mb-8" size="40px" />
				<div v-if="isRichTextMode" class="rich-text" v-html="sanitizedRichText"></div>
				<div v-else class="template-html" v-html="processedTemplateValue"></div>
				<div>
					<div class="line my-2"></div>
					<div v-if="shouldShowReportFooter" class="grey-text-styling py-1 ml-1">
						Didn't expect this email? <span class="cursor-pointer text-underline">Report it</span>
					</div>
					<template v-if="useWhiteLabelEntitlement" />
					<div v-else-if="showMinimalBranding" class="mt-2 grey-text-styling">
						Our processes run on <a class="grey-text-styling" href="https://ply.io" target="_blank"> Ply </a>
					</div>
					<a v-else class="router-container" href="https://ply.io" target="_blank">
						<AppIcon class="processes-on-ply pt-2" name="processes-on-ply" />
					</a>
				</div>
			</Col>
		</Col>
	</div>
</template>

<script>
import throttle from 'lodash/throttle';

const badgeParamValues = { FULL_BADGE: 'full_badge', MINIMAL_BADGE: 'minimal_badge', WHITE_LABEL: 'white_label' };
import { FeatureTypes } from '@/components/Billing/billing-types';
import AppDialogManagerService from '@/components/dialogs/AppDialogManager/app-dialog-manager-service';
import { getActionParams, getAction } from '@/store/modules/actions/getters';
import { getService } from '@/store/modules/apps/getters';
import { user } from '@/store/modules/auth/getters';
import { useEntitlement } from '@/store/modules/billing/getters';
import { editNode } from '@/store/modules/selectedNode/getters';
import { fetchEmailTemplates, fetchAllEmailTemplates } from '@/store/modules/transactionalEmailTemplate/actions';
import {
	useEmailTemplate,
	defaultEmailTemplate,
	selectedEmailTemplateId,
	usePublicEmailTemplate,
} from '@/store/modules/transactionalEmailTemplate/getters';
import { mapGettersShorthand, mapActionsShorthand } from '@/store/utils/mappers';
import { sanitizeHtml } from '@/utils/helpers';
import { getTestDialogParams } from '@/utils/transactional-emails-service';

export default {
	name: 'TransactionalEmailTabLayout',
	components: {},
	props: {
		processedEmailTemplateParams: { type: Object },
		showReportFooter: { type: Boolean },
		mode: { type: String },
		richText: { type: String },
		plyBrandingMode: { type: String },
	},
	data() {
		// If we don't use throttling for displaying the error message, it will be invoked too much
		// resulting in a bad experience + even making the browser unresponsive
		return { throttledShowToast: () => {} };
	},
	async mounted() {
		this.throttledShowToast = throttle(this.showToastError.bind(this), 10000);
		await this.fetchAllEmailTemplates();
	},
	computed: {
		...mapGettersShorthand({
			user,
			useEntitlement,
			useEmailTemplate,
			defaultEmailTemplate,
			selectedEmailTemplateId,
			usePublicEmailTemplate,
			getActionParams,
			getAction,
			editNode,
			getService,
		}),
		hasMinimalBrandingEntitlement() {
			return this.useEntitlement(FeatureTypes.CustomerMinimalBranding)?.has_access;
		},
		hasWhiteLabelEntitlement() {
			return this.useEntitlement(FeatureTypes.CustomerWhiteLabel)?.has_access;
		},
		shouldShowReportFooter() {
			return this.showReportFooter;
		},
		showMinimalBranding() {
			return this.plyBrandingMode === badgeParamValues.MINIMAL_BADGE && this.hasMinimalBrandingEntitlement;
		},
		useWhiteLabelEntitlement() {
			return this.plyBrandingMode === badgeParamValues.WHITE_LABEL && this.hasWhiteLabelEntitlement;
		},
		isPublicEmailTemplate() {
			return this.usePublicEmailTemplate(this.selectedEmailTemplateId);
		},
		selectedTemplate() {
			return this.useEmailTemplate(this.selectedEmailTemplateId);
		},
		isRichTextMode() {
			return this.mode === 'rich_text';
		},
		sanitizedRichText() {
			return sanitizeHtml(this.richText);
		},
		processedTemplateValue() {
			return this.replaceWithObjectValues(this.selectedTemplateText, this.processedEmailTemplateParams);
		},
		selectedTemplateText() {
			return this.selectedTemplate?.html;
		},
		isEmailContentLoading() {
			if (this.isRichTextMode) {
				return false;
			}
			return !this.processedTemplateValue;
		},
		nodeActionParams() {
			return this.getActionParams(this.editNode?.action);
		},
		nodeAction() {
			return this.getAction(this.editNode?.action);
		},
		nodeService() {
			return this.getService(this.editNode?.action);
		},
	},
	methods: {
		...mapActionsShorthand({ fetchEmailTemplates, fetchAllEmailTemplates }),
		showToastError(e) {
			this.$toast.error(e, { timeout: 5000 });
		},
		async openChangeTemplateDialog() {
			// Currently, if this.selectedTemplate is null (means selectedTemplateId has not loaded or email templates have not been fetched)
			// we open the default email template. Is this the preferred behavior or is it better to stall the dialog opening until the email templates are loaded?
			const templateToOpen = this.selectedTemplate || this.defaultEmailTemplate;
			await AppDialogManagerService.openChangeEmailTemplateDialog({ templateIdInTabPreview: templateToOpen.id });
		},
		async openEditTemplateDialog() {
			if (this.isPublicEmailTemplate) {
				this.$toast.error('Public templates cannot be edited');
				return;
			}
			let template = this.selectedTemplate;
			if (!template) {
				template = this.defaultEmailTemplate;
			}
			const editTemplateDialogRes = await AppDialogManagerService.openEditEmailTemplateDialog({
				templateId: template.id,
				templateTitle: template.title,
			});
			if (editTemplateDialogRes?.openChangeEmailTemplateDialog) {
				await AppDialogManagerService.openChangeEmailTemplateDialog({ templateIdInTabPreview: template.id });
			}
		},
		replaceWithObjectValues(template, replacements) {
			let errorMessage;
			if (!template) {
				return '';
			}
			let result = template;
			for (const key in replacements) {
				let regex;
				try {
					regex = new RegExp('{{' + key + '}}', 'g');
				} catch (error) {
					if (error.message.toLowerCase().includes('invalid regular expression')) {
						errorMessage = `"${key}" is not a valid field name\n Change it in the template's design.`;
					}
				}
				if (replacements[key]?.trim()) {
					result = result.replace(regex, replacements[key]);
				}
			}
			if (errorMessage) {
				// If we don't use throttling for displaying the error message, it will be invoked too much
				// resulting in a bad experience + even making the browser unresponsive
				this.throttledShowToast(errorMessage);
			}
			return result;
		},
		async sendTest() {
			const params = getTestDialogParams(this.nodeActionParams, this.nodeService, this.nodeAction, this.editNode, this.user.email);
			AppDialogManagerService.openTestStepWithReplacedParams(params);
		},
	},
};
</script>

<style scoped lang="scss">
.template-html {
	max-height: 100% !important;
}
::v-deep {
	.rich-text {
		p,
		span {
			font-size: 18px !important;
		}
	}
}

.processes-on-ply {
	justify-content: flex-start;
}
.grey-text-styling {
	color: var(--Colors-Neutral-Text-colorTextSecondary, #646e87);
	font-size: 12px;
	font-style: normal;
	font-weight: 400;
	line-height: 100%;
}
.line {
	width: 310px;
	height: 1px;
	background: #e0e2e7;
}
.change-template-btn {
	color: #6762f0;
	padding-top: 6px;
}
.header {
	border-bottom: 2px solid #e4e8f3;
	padding-bottom: 12px !important;
}
.header-btn {
	margin-left: 15px;
	border: solid 1px #6060f7;
	color: #6060f7;
	background-color: white !important;
	min-width: 80px;
	font-weight: 400 !important;
	text-align: center;
	font-size: 14px !important;
}

.main-template-background {
	display: flex;
	flex-direction: column;
	max-height: 92%;
	overflow-y: auto;
	padding: 10px;
	padding-bottom: 1%;
	.email-template-content {
		background: white;
		flex: 1 0 auto; /* Consume available space */
		overflow-y: auto;
		display: flex;
		flex-direction: column;
		.template-html {
			flex: 1 0 auto; /* Grow and shrink as needed */
			overflow-y: auto;
		}
	}
	@media (max-width: 1550px) {
		// @media (max-width: 1550px) {
		max-height: 88%;
	}
}
</style>
